import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import PageContainer from "~/components/PageContainer";
import MessagingEditor from "./Editor";

import { labelCategory, labelChannel, messagingRequest, errorText, messageSectionAccess, sections } from "./shared";

const MessagingGridContext = createContext({});
const deliveryHeaders = [
  { title: "Campaign", name: "campaign_name", type: "text", filter: false },
  { title: "Student", name: "student_name", type: "text", filter: false },
  { title: "Channel", name: "channel", type: "text", filter: false },
  { title: "Status", name: "status", type: "text", filter: false },
  { title: "Created on", name: "created_at", type: "date", filter: false },
];
const headers = {
  templates: [
    { title: "Name", name: "name", type: "text", filter: false },
    { title: "Category", name: "category", type: "text", filter: false },
    { title: "Status", name: "status", type: "text", filter: false },
    { title: "Created on", name: "created_at", type: "date", filter: false },
  ],
  campaigns: [
    { title: "Name", name: "name", type: "text", filter: false },
    { title: "Channels", name: "channels", type: "text", filter: false },
    { title: "Language", name: "language", type: "text", filter: false },
    { title: "Status", name: "status", type: "text", filter: false },
    { title: "Created on", name: "created_at", type: "date", filter: false },
  ],
  history: [
    { title: "Subject", name: "subject", type: "text", filter: false },
    { title: "Type", name: "type", type: "text", filter: false },
    { title: "Channel", name: "method", type: "text", filter: false },
    { title: "Created on", name: "created_at", type: "date", filter: false },
  ],
};
const statusLabel = (value) => ({ draft: "Draft", active: "Active", archived: "Archived", confirmed: "Confirmed" }[value] || value);

function PagePreview({ access, id, kind, filial, category }) {
  const { setOpened, reload, reportEditorState } = useContext(MessagingGridContext);
  return <MessagingEditor kind={kind} category={category} id={id} filial={filial} access={access.MenuHierarchyXGroup || access}
    onClose={() => { setOpened(null); reload(); }} onStateChange={reportEditorState}
    onSaved={(data) => {
      reportEditorState({ dirty: false, busy: false });
      if (kind === "campaigns" && data.status === "draft") setOpened(data.id);
      else { setOpened(null); reload(); }
    }} />;
}

export default function MessagingCatalog({ kind }) {
  const { filial, accesses } = useSelector((state) => state.auth);
  const alias = sections.find((section) => section.kind === kind).alias;
  const pageAccess = messageSectionAccess(accesses, alias);
  const access = pageAccess?.MenuHierarchyXGroup || {};
  return <Catalog key={String(filial?.id) + ":" + kind + ":" + JSON.stringify(access)} kind={kind} filial={filial} access={access} />;
}

export function Catalog({ kind, filial, access, category }) {
  const [historySource, setHistorySource] = useState("campaign");
  const campaignHistory = kind === "history" && historySource === "campaign";
  const currentHeaders = campaignHistory ? deliveryHeaders : headers[kind];
  const [gridData, setGridData] = useState([]);
  const [gridHeader, setGridHeader] = useState(currentHeaders);
  const [gridDetails, setGridDetails] = useState({ totalRows: 0, pages: 1 });
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [orderBy, setOrder] = useState({ column: "created_at", asc: false });
  const [search, setSearch] = useState(null);
  const [activeFilters, setActiveFilters] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [opened, setOpened] = useState(null);
  const [successfullyUpdated, setSuccessfullyUpdated] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const debounce = useRef(null);
  const busy = useRef(false);
  const reload = useCallback(() => setRevision((value) => value + 1), []);
  const reportEditorState = useCallback((state) => {
    busy.current = state.busy;
    setSuccessfullyUpdated(!state.dirty);
  }, []);
  const handleOpened = (id) => {
    if (busy.current) return;
    setSuccessfullyUpdated(true);
    setOpened(id);
  };
  const setOrderBy = (value) => {
    if (campaignHistory && !["channel", "status", "created_at"].includes(value.column)) return;
    if (kind === "history" && historySource === "quick" && !["subject", "created_at"].includes(value.column)) return;
    setPage(1); setOrder(value);
  };
  const changeHistorySource = (value) => {
    setHistorySource(value); setPage(1); setOpened(null); setSearch(null);
    clearTimeout(debounce.current);
    setOrder({ column: "created_at", asc: false });
    setGridHeader(value === "campaign" ? deliveryHeaders : headers.history);
  };
  const handleFilters = ({ title, value }) => {
    if (title !== "search") return;
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => { setPage(1); setSearch({ value }); }, 700);
  };

  useEffect(() => () => clearTimeout(debounce.current), []);
  useEffect(() => { if (!opened) { busy.current = false; setSuccessfullyUpdated(true); } }, [opened]);
  useEffect(() => {
    const controller = new AbortController();
    setError("");
    setGridData([]);
    if (!access.view || !filial?.id) { setLoadingData(false); return () => controller.abort(); }
    setLoadingData(true);
    messagingRequest(filial.id, "get", "/messaging/" + kind, undefined, controller.signal,
      { page, limit, search: search?.value || "", orderBy: orderBy.column, orderASC: orderBy.asc ? "ASC" : "DESC", ...(kind === "history" ? { source: historySource } : {}), ...(category ? { category } : kind !== "history" ? { exclude_category: "financial" } : {}) })
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        const rows = data.rows || [];
        if (rows.some((row) => Number(row.filial_id) !== Number(filial.id))) throw new Error("Branch mismatch");
        const totalRows = Number(data.totalRows) || 0;
        const count = Math.max(1, Math.ceil(totalRows / limit));
        if (Number(page) > count) { setPage(count); return; }
        setPages(count);
        setGridDetails({ totalRows, pages: count });
        setGridData(rows.map((row) => ({
          id: row.id, show: true, selectable: false, canceled: row.status === "archived", page: Number(page),
          fields: kind === "templates" ? [row.name, labelCategory(row.category), statusLabel(row.status), row.created_at]
            : kind === "campaigns" ? [row.name, row.channels?.map(labelChannel).join(", ") || "Not selected", row.language?.toUpperCase(), statusLabel(row.status), row.created_at]
            : campaignHistory ? [row.campaign_name, row.student_name || "Student", labelChannel(row.method), row.status === "accepted" ? "Accepted by provider" : row.status, row.created_at]
            : [row.subject || "Untitled message", row.type || "", row.method || "", row.created_at],
        })));
      })
      .catch((failure) => {
        if (controller.signal.aborted) return;
        setGridDetails({ totalRows: 0, pages: 1 });
        setPages(1);
        setError(failure.message === "Branch mismatch" ? "This response includes another branch. Select a specific location and reload." : errorText(failure));
      })
      .finally(() => { if (!controller.signal.aborted) setLoadingData(false); });
    return () => controller.abort();
  }, [access.view, filial?.id, kind, category, page, limit, orderBy, search, revision, historySource, campaignHistory]);

  if (!access.view || !filial?.id) return <div className="h-full flex-1 bg-white p-4"><p role="alert" className="text-sm text-gray-600">{!access.view ? "You do not have permission to view this Messages section." : "Select a location to manage messages."}</p></div>;

  return <MessagingGridContext.Provider value={{ gridData, setGridData, gridHeader, setGridHeader, gridDetails, setGridDetails,
    pages, setPages, page, setPage, limit, setLimit: (value) => { setPage(1); setLimit(value); },
    orderBy, setOrderBy, search, setSearch, activeFilters, setActiveFilters, loadingData, opened, setOpened,
    handleOpened, handleFilters, successfullyUpdated, setSuccessfullyUpdated, reload, reportEditorState }}>
    <PageContainer FullGridContext={MessagingGridContext} PagePreview={PagePreview} pageAccess={{ MenuHierarchyXGroup: access }}
      defaultGridHeader={currentHeaders} handleNew={kind !== "history"} extraPreviewProps={{ kind, filial, category }}
      previewControllerProps={{ allowTextAreaEnter: true }}
      headerContent={<>{kind === "history" && !category && <div className="flex flex-col items-start px-4 py-2 text-xs"><span className="px-1">History source</span><select className="border rounded-xs p-2 text-sm bg-white outline-hidden focus:border-mila_orange" value={historySource} onChange={event => changeHistorySource(event.target.value)}><option value="campaign">Campaign deliveries</option><option value="legacy">Previous messages</option><option value="quick">Quick messages</option></select></div>}{error && <p role="alert" className="p-2 text-xs text-red-700">{error}<button type="button" onClick={reload} className="ml-2 underline">Try again</button></p>}</>} />
  </MessagingGridContext.Provider>;
}
